import React, { useCallback, useEffect, useRef, useState } from 'react';

import axios from 'axios';

interface IAccountMetricsJson {
  cuft_used: number;
  unsized_items_count: number;
}

interface IUsageJson {
  legacy?: boolean;
  cubic_ft: number;
  updated_at: string;
  stale?: boolean;
}

interface IDisplayMetrics {
  legacy: boolean;
  cuft_used: number;
  updated_at?: string;
  stale?: boolean;
  all_categorized?: boolean;
  unsized_items_count: number;
}

const REFRESH_EVENT = 'admin:account-usages-refresh';
const ACCOUNTS_SHOW_REFRESH = 'admin:accounts-show-refresh';

interface IUsagesRefreshDetail {
  accountId?: string | number;
  /** When set (e.g. from ItemCategorizationObserverService.notify), reuse this instead of a second metrics.json fetch. */
  metricsPromise?: Promise<IAccountMetricsJson>;
}

/**
 * Space used / calculated line for account summary and inventory header.
 * Loads `/accounts/:id/metrics.json` and optionally legacy `/accounts/:id/usage.json`.
 */
export const AccountUsagesWidget: React.FC<{ accountID: string }> = ({ accountID }) => {
  const [metrics, setMetrics] = useState<IDisplayMetrics | null>(null);
  const latestLoadRequestIdRef = useRef(0);

  const load = useCallback(
    async (options?: { metricsPromise?: Promise<IAccountMetricsJson> }) => {
      if (!accountID) return;

      const requestId = ++latestLoadRequestIdRef.current;

      try {
        const rawMetrics = options?.metricsPromise
          ? await options.metricsPromise
          : (await axios.get<IAccountMetricsJson>(`/accounts/${accountID}/metrics.json`)).data;
        if (requestId !== latestLoadRequestIdRef.current) return;

        const next: IDisplayMetrics = {
          legacy: false,
          cuft_used: rawMetrics.cuft_used,
          unsized_items_count: rawMetrics.unsized_items_count,
        };

        if (rawMetrics.unsized_items_count <= 0) {
          next.all_categorized = true;
          if (requestId !== latestLoadRequestIdRef.current) return;
          setMetrics(next);
          return;
        }

        try {
          const { data: usage } = await axios.get<IUsageJson>(`/accounts/${accountID}/usage.json`);
          if (requestId !== latestLoadRequestIdRef.current) return;
          if (usage.legacy) {
            next.legacy = true;
            next.cuft_used = usage.cubic_ft;
            next.updated_at = usage.updated_at;
            next.stale = usage.stale;
          }
        } catch {
          // No usage row (404) or error — keep metrics-only display
        }

        if (requestId !== latestLoadRequestIdRef.current) return;
        setMetrics(next);
      } catch {
        if (requestId !== latestLoadRequestIdRef.current) return;
        setMetrics(null);
      }
    },
    [accountID],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onUsagesRefresh = (event: Event) => {
      const rawDetail = (event as CustomEvent<IUsagesRefreshDetail>).detail;
      const detailAccountId = rawDetail?.accountId != null ? String(rawDetail.accountId) : undefined;
      const normalizedAccountId = String(accountID);
      if (detailAccountId && detailAccountId !== normalizedAccountId) return;
      void load(rawDetail?.metricsPromise ? { metricsPromise: rawDetail.metricsPromise } : undefined);
    };

    const onAccountsShowRefresh = () => {
      void load();
    };

    document.addEventListener(REFRESH_EVENT, onUsagesRefresh);
    document.addEventListener(ACCOUNTS_SHOW_REFRESH, onAccountsShowRefresh);
    return () => {
      document.removeEventListener(REFRESH_EVENT, onUsagesRefresh);
      document.removeEventListener(ACCOUNTS_SHOW_REFRESH, onAccountsShowRefresh);
    };
  }, [accountID, load]);

  if (!metrics) {
    return null;
  }

  const updatedLabel =
    metrics.updated_at &&
    new Date(metrics.updated_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' });

  const uncategorizedLabel = metrics.unsized_items_count === 1 ? 'item' : 'items';

  return (
    <div>
      <span>
        <strong>{metrics.legacy ? 'Space Used:' : 'Space Calculated:'}</strong>{' '}
        <span>
          {metrics.cuft_used.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} cubic
          feet
        </span>
        {updatedLabel && <span> (updated on {updatedLabel})</span>}
        {metrics.all_categorized && <span> (up to date)</span>}
        {metrics.legacy && metrics.stale && <span className="label label-danger">Outdated</span>}
      </span>
      {metrics.unsized_items_count > 0 && (
        <span className="text-danger">
          {' '}
          - {metrics.unsized_items_count} {uncategorizedLabel} uncategorized
        </span>
      )}
    </div>
  );
};
